import React, { useEffect, useState } from 'react';
import { authAPI } from '../../../services/api';
import './employees.css'

/* ─────────────────────────────────────────────
   All class names are prefixed with "ep__"
   to avoid colliding with any global CSS.
───────────────────────────────────────────── */

const Employees = () => {
  const [users, setUsers]           = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast]           = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await authAPI.getEmployees();
      setUsers(res.data);
    } catch {
      showToast('Failed to load employees.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEdit   = (user) => setEditingUser({ ...user });
  const handleChange = (e)    => setEditingUser({ ...editingUser, [e.target.name]: e.target.value });

  const handleUpdate = async () => {
    try {
      setSaving(true);
      await authAPI.updateUser(editingUser.id, editingUser);
      setEditingUser(null);
      fetchUsers();
      showToast('Employee updated successfully!');
    } catch {
      showToast('Update failed. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const COLORS = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#0284c7'];
  const getColor    = (name) => COLORS[(name?.charCodeAt(0) || 0) % COLORS.length];
  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <>
      {/* ═══════════════════════════════════════
          STYLES  — fully scoped with ep__ prefix
      ═══════════════════════════════════════ */}
      
      <div className="ep__wrap">

        {/* ── Top bar ── */}
        <div className="ep__topbar">
          <div className="ep__title-row">
            <div className="ep__icon-box">
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div>
              <h2 className="ep__h2">Employees</h2>
              <p className="ep__sub">{users.length} total members</p>
            </div>
          </div>

          {/* FIXED search — icon is positioned, input has enough left-padding */}
          <div className="ep__search-box">
            {/* <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg> */}
            <input
              className="ep__search-input"
              type="text"
              placeholder='Search here...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="ep__card">
          <table className="ep__table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Login Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 7 }).map((_, i) => (
                  <tr key={i}>
                    {[130, 70, 160, 95, 64].map((w, j) => (
                      <td key={j}><span className="ep__skel" style={{ width: w }} /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan="5">
                  <div className="ep__empty">
                    <svg width="38" height="38" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                    </svg>
                    <p>No employees found{searchTerm ? ` for "${searchTerm}"` : ''}.</p>
                  </div>
                </td></tr>
              ) : (
                filtered.map(user => (
                  <tr key={user.id}>
                    <td>
                      <div className="ep__name-cell">
                        <div className="ep__avatar" style={{ background: getColor(user.name) }}>
                          {getInitials(user.name)}
                        </div>
                        <span className="ep__name">{user.name}</span>
                      </div>
                    </td>
                    <td>
                      {user.loginName
                        ? <span>{user.loginName}</span>
                        : <span className="ep__dash">—</span>}
                    </td>
                    <td>
                      <span className="ep__email-cell">
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/>
                        </svg>
                        {user.email}
                      </span>
                    </td>
                    <td>
                      {user.mobile
                        ? <span className="ep__mobile">{user.mobile}</span>
                        : <span className="ep__na">Not added</span>}
                    </td>
                    <td>
                      <button className="ep__edit-btn" onClick={() => handleEdit(user)}>
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/>
                        </svg>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {!loading && filtered.length > 0 && (
            <div className="ep__foot">
              Showing {filtered.length} of {users.length} employees
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════
            MODAL — fixed overlay, self-contained
        ════════════════════════════════════════ */}
        {editingUser && (
          <div
            className="ep__overlay"
            onMouseDown={e => e.target === e.currentTarget && setEditingUser(null)}
          >
            <div className="ep__modal">

              {/* ✕ close */}
              <button className="ep__modal-close" onClick={() => setEditingUser(null)}>
                <svg width="16" height="16" fill="none" stroke="#1f2937" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>

              {/* header — stays inside modal in normal flow */}
              <div className="ep__modal-head">
                <div className="ep__modal-avatar" style={{ background: getColor(editingUser.name) }}>
                  {getInitials(editingUser.name)}
                </div>
                <div>
                  <h3 className="ep__modal-htitle">Edit Employee</h3>
                  <p className="ep__modal-hsub">Update {editingUser.name}'s details</p>
                </div>
              </div>

              {/* 2-col grid form */}
              <div className="ep__form-grid">

                <div className="ep__fg">
                  <label className="ep__label">Full Name</label>
                  <input
                    className="ep__input"
                    name="name"
                    value={editingUser.name || ''}
                    onChange={handleChange}
                    placeholder="Full name"
                  />
                </div>

                <div className="ep__fg">
                  <label className="ep__label">Login Name</label>
                  <input
                    className="ep__input"
                    name="loginName"
                    value={editingUser.loginName || ''}
                    onChange={handleChange}
                    placeholder="Login username"
                  />
                </div>

                <div className="ep__fg full">
                  <label className="ep__label">Email Address</label>
                  <input
                    className="ep__input"
                    name="email"
                    type="email"
                    value={editingUser.email || ''}
                    onChange={handleChange}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="ep__fg full">
                  <label className="ep__label">Mobile Number</label>
                  <input
                    className="ep__input"
                    name="mobile"
                    value={editingUser.mobile || ''}
                    onChange={handleChange}
                    placeholder="+91 00000 00000"
                  />
                </div>

              </div>

              <div className="ep__divider" />

              <div className="ep__actions">
                <button className="ep__btn-cancel" onClick={() => setEditingUser(null)}>
                  Cancel
                </button>
                <button className="ep__btn-save" onClick={handleUpdate} disabled={saving}>
                  {saving ? (
                    <><span className="ep__spinner" /> Saving…</>
                  ) : (
                    <>
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                        <polyline points="17 21 17 13 7 13 7 21"/>
                        <polyline points="7 3 7 8 15 8"/>
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>

            </div>
          </div>
        )}

        {/* ── Toast ── */}
        {toast && (
          <div className={`ep__toast ${toast.type === 'error' ? 'ep__toast--err' : 'ep__toast--ok'}`}>
            {toast.type === 'error' ? (
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
              </svg>
            ) : (
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
            )}
            {toast.message}
          </div>
        )}

      </div>
    </>
  );
};

export default Employees;